import { SimpleModalService } from '../../../shared/dialogs/simple-modal.service'
import { Inject, Injectable } from '@angular/core'
import { AuthContextStorageService } from '../../../core/auth/auth-context-storage.service'
import { AttachmentRouteTypeEnum } from '../enums/attachment-route-type.enum'
import { Observable, firstValueFrom } from 'rxjs'
import {
    AttachmentControllerService,
    AttachmentDto,
    AttachmentLinkDto,
    AttachmentOnlineDocumentControllerService, AttachmentUploadedType,
    BarterControllerService,
    BuildingHeaderService,
    ContractControllerService,
    ContractSignatureControllerService,
    ListResponseAttachmentDto,
    ListResponsePurchaseCaseFileDto,
    NonCadastralContractControllerService,
    ParcelControllerService,
    PurchaseCaseAttachmentControllerService,
    PurchaseCaseFileDto,
    SubjectControllerService,
} from '../../../api/backend'
import { CommonService } from '../../../core/services/common.service'
import { HttpResponse } from '@angular/common/http'
import { DialogService } from '../../../core/services/dialog.service'
import { FileDownloadService } from '../../../core/services/fileDownload.service'
// Dialog components are imported dynamically to avoid circular dependency
// (dialogs import AttachmentService, which imports dialogs)

@Injectable({
    providedIn: 'root'
})
export class AttachmentService {

    constructor(@Inject(AttachmentControllerService) private attachmentControllerService: AttachmentControllerService,
                @Inject(AttachmentOnlineDocumentControllerService) private attachmentOnlineDocumentControllerService: AttachmentOnlineDocumentControllerService,
                @Inject(BarterControllerService) private barterControllerService: BarterControllerService,
                @Inject(BuildingHeaderService) private buildingHeaderService: BuildingHeaderService,
                @Inject(CommonService) private commonService: CommonService,
                @Inject(ContractControllerService) private contractControllerService: ContractControllerService,
                @Inject(ContractSignatureControllerService) private contractSignatureControllerService: ContractSignatureControllerService,
                @Inject(DialogService) private dialogService: DialogService,
                @Inject(FileDownloadService) private fileDownloadService: FileDownloadService,
                @Inject(NonCadastralContractControllerService) private nonCadastralContractControllerService: NonCadastralContractControllerService,
                @Inject(ParcelControllerService) private parcelControllerService: ParcelControllerService,
                @Inject(PurchaseCaseAttachmentControllerService) private purchaseCaseAttachmentControllerService: PurchaseCaseAttachmentControllerService,
                @Inject(SimpleModalService) private simpleModalService: SimpleModalService,
                @Inject(SubjectControllerService) private subjectControllerService: SubjectControllerService,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
                ) {
    }

    // @TODO: Make it ng2 structural directive after migration
    endsWithExtension(fileName: string, extensions: string[]): boolean {
        return extensions.some(extension => fileName.toLowerCase().endsWith('.' + extension.toLowerCase()))
    }

    getAttachments(routeType: AttachmentRouteTypeEnum, entityId: number, attachmentsByContractGroupId?: boolean): Promise<AttachmentDto[]|PurchaseCaseFileDto[]> {
        let observable: Observable<ListResponseAttachmentDto|ListResponsePurchaseCaseFileDto>

        switch (routeType) {
            case AttachmentRouteTypeEnum.CONTRACT:
                observable = this.contractControllerService.getContractAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    attachmentsByContractGroupId
                )
                break
            case AttachmentRouteTypeEnum.BARTER:
                observable = this.barterControllerService.getBarterAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId
                )
                break
            case AttachmentRouteTypeEnum.PURCHASE:
                observable = this.purchaseCaseAttachmentControllerService.getPurchaseCaseAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId
                )
                break
            case AttachmentRouteTypeEnum.PARCEL:
                observable = this.parcelControllerService.getParcelAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId
                )
                break
            case AttachmentRouteTypeEnum.BUILDING:
                observable = this.buildingHeaderService.getBuildingAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId
                )
                break
            case AttachmentRouteTypeEnum.SUBJECT:
                observable = this.subjectControllerService.getSubjectContactAttachments(
                    this.authContextStorageService.getProjectKey(),
                    entityId
                )
                break
        }

        if (observable) {
            return new Promise(resolve => {
                observable.subscribe((data: ListResponseAttachmentDto|ListResponsePurchaseCaseFileDto) => {
                    resolve(data.items)
                })
            })
        } else {
            return Promise.resolve([])
        }
    }

    previewContractSignatureDocument(contractId, documentId): void {
        this.commonService.showActionProgress('Probíhá generování náhledu.')

        this.contractSignatureControllerService.getSignatureDocumentPreview(
            this.authContextStorageService.getProjectKey(),
            contractId,
            documentId,
            'body',
            false,
            { httpHeaderAccept: 'application/pdf' } //CA-16663 + CA-16753
        ).subscribe((data: any) => {
            this.handleDisplayPreview(new Blob([data], {type: data.type}))
            this.commonService.hideActionProgress()
        }, (err) => this.handleDisplayPreviewError(err))
    }

    downloadAttachment(attachmentId: number, routeType: AttachmentRouteTypeEnum): Observable<HttpResponse<Blob>> {
        switch (routeType) {
            case AttachmentRouteTypeEnum.BARTER:
            case AttachmentRouteTypeEnum.CONTRACT:
            case AttachmentRouteTypeEnum.PARCEL:
            case AttachmentRouteTypeEnum.BUILDING:
            case AttachmentRouteTypeEnum.SUBJECT:
                return this.attachmentControllerService.getAttachmentData(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId,
                    'response'
                )
            case AttachmentRouteTypeEnum.PURCHASE:
                return this.purchaseCaseAttachmentControllerService.getPurchaseCaseAttachmentData(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId,
                    'response'
                )
        }
    }

    getAttachmentEditLink(attachmentId): Promise<any> {
        return new Promise(resolve => {
            // @ts-ignore
            this.attachmentOnlineDocumentControllerService.startOnlineDocument(
                this.authContextStorageService.getProjectKey(),
                attachmentId,
                'body',
                false,
                { httpHeaderAccept: 'text/plain' }
            ).subscribe((response: string) => {
                resolve(response)
            })
        })
    }

    getOnlineDocumentEditApproval(attachmentId: number): Promise<any> {
        return new Promise<any>(resolve => {
            this.attachmentOnlineDocumentControllerService.existsApproval(
                this.authContextStorageService.getProjectKey(),
                attachmentId
            ).subscribe(value => {
                resolve(value)
            })
        })
    }

    isAttachmentUrlWithProtocol(url): boolean {
        if (typeof url === 'string') {
            const test = url.toLowerCase()
            return /^(http|https|file|ssh|ssl|ftp)/.test(test)
        }
        return false
    }

    openOffice365Window(externalLink): void {
        // const newWindow = window.open(externalLink, '_blank')
        let secureUrl = this.addHttpsIfMissing(externalLink)
        const newWindow = window.open(secureUrl, secureUrl)
        if (newWindow === null || typeof(newWindow) == 'undefined') {
            console.error('Failed to open the new window.')
        }
    }

    saveLinkAttachment(name, url, entityId, routeType: AttachmentRouteTypeEnum): Promise<void> {
        let observable: Observable<void>

        const dto: AttachmentLinkDto = {
            name: name,
            url: url
        }

        switch (routeType) {
            case AttachmentRouteTypeEnum.CONTRACT:
                observable = this.contractControllerService.uploadContractLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
            case AttachmentRouteTypeEnum.BARTER:
                observable = this.barterControllerService.uploadBarterLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
            case AttachmentRouteTypeEnum.NONCADASTRAL_CONTRACT:
                observable = this.nonCadastralContractControllerService.uploadNonCadastralLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
            case AttachmentRouteTypeEnum.PARCEL:
                observable = this.parcelControllerService.uploadParcelLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
            case AttachmentRouteTypeEnum.BUILDING:
                observable = this.buildingHeaderService.uploadBuildingLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
            case AttachmentRouteTypeEnum.SUBJECT:
                observable = this.subjectControllerService.uploadSubjectContactLinkAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    dto
                )
                break
        }

        if (observable) {
            return new Promise(resolve => {
                observable.subscribe(() => {
                    resolve()
                })
            })
        } else {
            return Promise.resolve()
        }
    }

    saveEditedScan(routeType: AttachmentRouteTypeEnum, attachmentId, uploadedType, subjectIds: number[]): Promise<void> {
        let observable: Observable<void>

        switch (routeType) {
            case AttachmentRouteTypeEnum.CONTRACT:
                observable = this.contractControllerService.editContractAttachmentScan(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId,
                    uploadedType,
                    subjectIds
                )
                break
            case AttachmentRouteTypeEnum.NONCADASTRAL_CONTRACT:
                observable = this.nonCadastralContractControllerService.editNonCadastralAttachmentScan(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId,
                    uploadedType,
                    subjectIds
                )
        }

        if (observable) {
            return new Promise(resolve => {
                observable.subscribe(() => resolve())
            })
        } else {
            return Promise.resolve()
        }
    }

    setOnlineDocumentEditApproval(attachmentId: number): Promise<any> {
        return new Promise<any>(resolve => {
            this.attachmentOnlineDocumentControllerService.approve(
                this.authContextStorageService.getProjectKey(),
                attachmentId
            ).subscribe(value => {
                resolve(value)
            })
        })
    }

    deleteAttachment(attachmentId: number, routeType: AttachmentRouteTypeEnum): Promise<any> {
        switch (routeType) {
            case AttachmentRouteTypeEnum.BARTER:
            case AttachmentRouteTypeEnum.CONTRACT:
            case AttachmentRouteTypeEnum.PARCEL:
            case AttachmentRouteTypeEnum.BUILDING:
            case AttachmentRouteTypeEnum.SUBJECT:
                return firstValueFrom(this.attachmentControllerService.deleteAttachment(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId
                ))
            case AttachmentRouteTypeEnum.PURCHASE:
                return firstValueFrom(this.purchaseCaseAttachmentControllerService.deletePurchaseCaseAttachment(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId
                ))
        }

        return Promise.resolve()
    }

    displayAttachmentPreview(attachmentId, routeType: AttachmentRouteTypeEnum): void {
        let observable: Observable<Blob>

        this.commonService.showActionProgress('Probíhá generování náhledu.')

        switch (routeType) {
            case AttachmentRouteTypeEnum.BARTER:
            case AttachmentRouteTypeEnum.CONTRACT:
            case AttachmentRouteTypeEnum.PARCEL:
            case AttachmentRouteTypeEnum.BUILDING:
            case AttachmentRouteTypeEnum.SUBJECT:
                observable = this.attachmentControllerService.getAttachmentPreview(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId
                )
                break
            case AttachmentRouteTypeEnum.PURCHASE:
                observable = this.purchaseCaseAttachmentControllerService.getPurchaseCaseAttachmentPreview(
                    this.authContextStorageService.getProjectKey(),
                    attachmentId
                )
                break
        }

        if (observable) {
            observable.subscribe((data: Blob) => {
                this.handleDisplayPreview(data)
            }, (err) => this.handleDisplayPreviewError(err))
        } else {
            this.commonService.hideActionProgress()
        }

    }

    displaySignedDocumentDraft(contractId): void {
        this.commonService.showActionProgress('Probíhá generování náhledu.')

        this.contractSignatureControllerService.getSignatureDraftDocumentPreview(
            this.authContextStorageService.getProjectKey(),
            contractId,
            'body',
            false,
            { httpHeaderAccept: 'application/pdf' } // DEV-791, see also CA-16663 + CA-16753
        ).subscribe((data: any) => {
            this.handleDisplayPreview(new Blob([data], {type: data.type}))

            this.commonService.hideActionProgress()
        }, (err) => this.handleDisplayPreviewError(err))
    }

    async showAddLinkAttachmentDialog(routeType: AttachmentRouteTypeEnum, entityId: number, name?: string, url?: string): Promise<boolean> {
        const { AddLinkAttachmentDialogComponent } = await import('../dialogs/add-link-attachment-dialog/add-link-attachment-dialog.component')
        return new Promise<boolean>(resolve => {
            this.simpleModalService.addModal(AddLinkAttachmentDialogComponent, {
                name: name,
                url: url,
                routeType: routeType,
                entityId: entityId
            }).subscribe((result: boolean) => resolve(result))
        })
    }

    async showAddAttachmentDialog(routeType: AttachmentRouteTypeEnum, entityId: number, task: string, mode: string, ownersLengthInput$?: Observable<number>): Promise<boolean> {
        const { AddAttachmentDialogComponent } = await import('../dialogs/add-attachment-dialog/add-attachment-dialog.component')
        return new Promise<boolean>(resolve => {
            this.simpleModalService.addModal(AddAttachmentDialogComponent, {
                entityId: entityId,
                task: task,
                mode: mode,
                routeType: routeType,
                ownersLengthInput$: ownersLengthInput$
            }).subscribe((result: boolean) => resolve(result))
        })
    }

    async showEditDocxDialog(attachmentId: number, onlineDocumentLink: string, task?: string): Promise<boolean> {
        const { EditDocxDialogComponent } = await import('../dialogs/edit-docx-dialog/edit-docx-dialog.component')
        return firstValueFrom(this.simpleModalService.addModal(EditDocxDialogComponent, {
            attachmentId: attachmentId,
            onlineDocumentLink: onlineDocumentLink,
            task: task
        }))
    }

    async showEditScanDialog(routeType: AttachmentRouteTypeEnum, entityId: number, task: string, mode: string): Promise<boolean> {
        const { EditScanDialogComponent } = await import('../dialogs/edit-scan-dialog/edit-scan-dialog.component')
        return firstValueFrom(this.simpleModalService.addModal(EditScanDialogComponent, {
            routeType: routeType,
            entityId: entityId,
            task: task,
            mode: mode,
        }))
    }

    async showEditDocxTermsDialog(): Promise<boolean> {
        const { EditDocxTermsDialogComponent } = await import('../dialogs/edit-docx-terms-dialog/edit-docx-terms-dialog.component')
        return new Promise<boolean>(resolve => {
            this.simpleModalService.addModal(EditDocxTermsDialogComponent).subscribe((result: boolean) => resolve(result))
        })
    }

    uploadAttachment(routeType: AttachmentRouteTypeEnum, entityId: number, filename: string, file: Blob): Promise<void> {
        let observable: Observable<any>

        switch (routeType) {
            case AttachmentRouteTypeEnum.BARTER:
                observable = this.barterControllerService.uploadBarterAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.CONTRACT:
                observable = this.contractControllerService.uploadContractAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.NONCADASTRAL_CONTRACT:
                observable = this.nonCadastralContractControllerService.uploadNonCadastralAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.PURCHASE:
                observable = this.purchaseCaseAttachmentControllerService.uploadPurchaseCaseAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.PARCEL:
                observable = this.parcelControllerService.uploadParcelAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.BUILDING:
                observable = this.buildingHeaderService.uploadBuildingAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.SUBJECT:
                observable = this.subjectControllerService.uploadSubjectContactAttachment(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    filename,
                    file
                )
                break
        }

        if (observable) {
            return new Promise(resolve => {
                observable.subscribe(() => resolve())
            })
        } else {
            return Promise.resolve()
        }
    }

    uploadAttachmentScan(routeType: AttachmentRouteTypeEnum, entityId: number, filename: string, file: Blob, uploadedType: AttachmentUploadedType, subjectIds: number[]): Promise<void> {
        let observable: Observable<any>

        switch (routeType) {
            case AttachmentRouteTypeEnum.CONTRACT:
                observable = this.contractControllerService.uploadContractAttachmentScan(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    uploadedType,
                    subjectIds,
                    filename,
                    file
                )
                break
            case AttachmentRouteTypeEnum.NONCADASTRAL_CONTRACT:
                observable = this.nonCadastralContractControllerService.uploadNonCadastralAttachmentScan(
                    this.authContextStorageService.getProjectKey(),
                    entityId,
                    uploadedType,
                    filename,
                    subjectIds,
                    file
                )
                break
        }

        if (observable) {
            return new Promise(resolve => {
                observable.subscribe(() => resolve())
            })
        } else {
            return Promise.resolve()
        }
    }

    handleDisplayPreviewError(err?): void {
        this.commonService.hideActionProgress()
        // can't use ErrorService because of response type blob
        this.dialogService.showWarningDialog('Náhled souboru nelze zobrazit.')

        console.error(err)
    }

    handleDisplayPreview(blob: Blob): void {
        this.commonService.hideActionProgress()
        if (blob?.size == 0) {
            this.dialogService.showWarningDialog('Nulová velikost souboru.')
            return
        }

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        // see https://blog.jayway.com/2017/07/13/open-pdf-downloaded-api-javascript/
        // @ts-ignore
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            // @ts-ignore
            window.navigator.msSaveOrOpenBlob(blob)
        } else {
            let url = window.URL.createObjectURL(blob)

            window.open(url)
        }
    }

    private addHttpsIfMissing(url: string): string {
        if (!url.startsWith('https://')) {
            return 'https://' + url
        }
        return url
    }
}